/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Thu Feb 22 2024
 **/

import { useRef, useState } from "react";
import AvatarEditor, { type Position } from "react-avatar-editor";

import { _T } from "@rfice/language";
import { Button, Column, Divider, Icon, Row, Typography } from "@/frameworks/designStuff/components";

import { Input } from "@/frameworks/core/components";

type ProfileState = {
	image: string | File;
	allowZoomOut: boolean;
	position: Position;
	scale: number;
	rotate: number;
	borderRadius: number;
	preview?: {
		img: string;
		rect: {
			x: number;
			y: number;
			width: number;
			height: number;
		};
		scale: number;
		width: number;
		height: number;
		borderRadius: number;
	};
	width: number;
	height: number;
	disableCanvasRotation: boolean;
	isTransparent: boolean;
	backgroundColor?: string;
	showGrid: boolean;
};
const MAX_ZOOM = 2;

export default function ProfileEditor({
	src,
	handleImage,
	onHide
}: {
	src: string | File;
	handleImage: (file: Blob) => void;
	onHide: () => void;
}) {
	const editor = useRef<AvatarEditor>(null);
	const [state, setState] = useState<ProfileState>({
		image: src,
		allowZoomOut: false,
		position: { x: 0.5, y: 0.5 },
		scale: 1,
		rotate: 0,
		borderRadius: 100,
		preview: undefined,
		width: 320,
		height: 320,
		disableCanvasRotation: false,
		isTransparent: false,
		showGrid: false
	});
	const handlePositionChange = (position: Position) => {
		setState({ ...state, position });
	};
	const handleSave = async () => {
		editor.current?.getImageScaledToCanvas().toBlob(
			(blobFile) => {
				if (blobFile) {
					handleImage(blobFile);
				}
			},
			"image/jpeg",
			0.95
		);
		onHide();
	};
	const handleScale = (e: React.ChangeEvent<HTMLInputElement>) => {
		const scale = parseFloat(e.target.value);
		setState({ ...state, scale });
	};
	return (
		<Column style={{ width: "440px" }}>
			<Column style={{ padding: "28px 32px 32px 32px", width: "100%" }} gap={20}>
				<Typography typoSize="Head6">{_T("프로필 사진 편집")}</Typography>
				<div style={{ width: "100%" }}>
					<AvatarEditor
						onPositionChange={handlePositionChange}
						ref={editor}
						scale={state.scale}
						width={state.width}
						height={state.height}
						position={state.position}
						color={[46, 47, 50, 0.76]}
						rotate={state.rotate}
						borderRadius={state.width / (100 / state.borderRadius)}
						image={state.image}
						disableCanvasRotation={state.disableCanvasRotation}
					/>
				</div>
				<Column style={{ width: "100%" }} horizonAlign="center" gap={10}>
					<Row style={{ width: "70%" }} horizonAlign="distribute">
						<Icon name="Minus24" size={20} />
						<Icon name="Add24" size={20} />
					</Row>
					<Input
						type="range"
						min={1}
						max={MAX_ZOOM}
						id="myRange"
						defaultValue="1"
						step="0.01"
						onChange={handleScale}
						css={{
							width: "70%"
						}}
					/>
				</Column>
			</Column>
			<Divider type="line" />
			<Row style={{ width: "100%", padding: "10px" }} horizonAlign="center" gap={8}>
				<Button size="large" filled="secondary" onClick={onHide}>
					{_T("취소")}
				</Button>
				<Button size="large" filled="primary" onClick={handleSave}>
					{_T("확인")}
				</Button>
			</Row>
		</Column>
	);
}
